import swal from "sweetalert";
import React, { useState } from 'react';
import axios from '../../services/AxiosInstance';
import { useHistory } from "react-router-dom";
import { getApiUrl } from "../../utils/ApiUtils";
import {
  Building2,
  Search,
  MapPin,
  Mail,
  Phone,
  ArrowLeft,
  Check,
  AlertCircle,
  FileText,
  Calendar,
  Edit3,
  X
} from 'lucide-react';
import useWindowSize, { isMobile } from '../../hooks/useWindowSize';

import loginbg from "../../images/bg-login.jpg";
import { useSelector } from "react-redux";
import { getUid } from "../../store/selectors/AuthSelectors";

// Enhanced CSS styles
const styles = {
  newCompanyPage: {
    minHeight: '100vh',
    display: 'flex',
    backgroundImage: `url(${loginbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  pageWrapper: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh'
  },
  leftPanel: {
    flex: '1',
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    color: 'white'
  },
  rightPanel: {
    flex: '1',
    background: 'rgba(255, 255, 255, 0.98)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backdropFilter: 'blur(10px)',
    overflowY: 'auto'
  },
  contentCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '3rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '700px',
    border: '1px solid #e5e7eb',
    margin: '2rem 0'
  },
  headerSection: {
    marginBottom: '2rem',
    textAlign: 'center'
  },
  formGroup: {
    marginBottom: '1.5rem',
    position: 'relative'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  formInput: {
    width: '100%',
    padding: '0.875rem 1rem 0.875rem 3rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa'
  },
  formSelect: {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa'
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    color: '#9ca3af',
    zIndex: 2
  },
  searchButton: {
    padding: '0.875rem 1.5rem',
    background: '#44814e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  submitButton: {
    width: '100%',
    padding: '0.875rem',
    background: '#44814e',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    marginTop: '1rem'
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.875rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    transition: 'color 0.2s ease',
    marginTop: '1rem'
  },
  searchResults: {
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginTop: '1rem',
    maxHeight: '300px',
    overflowY: 'auto'
  },
  companyResult: {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  companyResultHover: {
    backgroundColor: '#f0fdf4',
    borderColor: '#44814e'
  },
  selectedBadge: {
    background: '#44814e',
    color: 'white',
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem'
  },
  manualToggle: {
    background: 'none',
    border: 'none',
    color: '#44814e',
    fontSize: '0.875rem',
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: '0.5rem 0'
  },
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Mobile-specific style overrides (applied when screen ≤ 768px)
const mobileStyles = {
  pageWrapper: {
    flexDirection: 'column'
  },
  leftPanel: {
    minHeight: '200px',
    padding: '2rem 1.5rem',
    flex: 'none'
  },
  rightPanel: {
    padding: '1.5rem 1rem',
    flex: 'none'
  },
  contentCard: {
    padding: '2rem 1.5rem',
    boxShadow: 'none',
    borderRadius: '0'
  },
  formInput: {
    fontSize: '16px' // Prevents zoom on iOS
  },
  formSelect: {
    fontSize: '16px' // Prevents zoom on iOS
  }
};

// Helper function to merge mobile styles
const getResponsiveStyle = (baseStyle, mobileStyle, isMobile) => {
  return isMobile && mobileStyle ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

function NewCompany(props) {
  const history = useHistory();
  const uid = useSelector((state) => getUid(state));
  
  // Mobile responsiveness
  const { width } = useWindowSize();
  const mobile = isMobile(width);

  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [manualEntry, setManualEntry] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({
    companyName: '',
    orgNumber: '',
    street: '',
    postalCode: '',
    city: '',
    accountingMethod: '',
    vatReportingPeriod: '',
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredResult, setHoveredResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Registration method selection
  const [registrationMethod, setRegistrationMethod] = useState(null); // 'search', 'manual', 'sie'
  
  // Feature flag for SIE upload
  const isSieUploadEnabled = process.env.REACT_APP_ENABLE_SIE_REGISTRATION === 'true';
  
  // SIE file upload state
  const [sieFile, setSieFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [sieLoading, setSieLoading] = useState(false);
  const [sieError, setSieError] = useState(null);

  // Swedish business validation functions
  const validateSwedishOrgNumber = (orgNumber) => {
    // Remove any spaces, dashes, or other non-digit characters
    const cleaned = orgNumber.replace(/\D/g, '');

    // Must be exactly 10 digits
    if (cleaned.length !== 10) {
      return false;
    }

    // Swedish organization numbers use Luhn algorithm for checksum validation
    const digits = cleaned.split('').map(Number);
    let sum = 0;

    for (let i = 0; i < 9; i++) {
      let digit = digits[i];
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) {
          digit = digit - 9;
        }
      }
      sum += digit;
    }

    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit === digits[9];
  };

  const validateSwedishPostalCode = (postalCode) => {
    // Swedish postal codes are 5 digits, optionally with spaces (XXX XX)
    const cleaned = postalCode.replace(/\s/g, '');
    return /^\d{5}$/.test(cleaned);
  };

  const validateCompanyName = (name) => {
    // Company name should be at least 2 characters and max 100 characters
    // Should not contain only special characters
    if (!name || name.trim().length < 2 || name.trim().length > 100) {
      return false;
    }

    // Should contain at least some alphanumeric characters
    return /[a-zA-ZåäöÅÄÖ0-9]/.test(name);
  };

  const validateFormData = () => {
    const errors = [];

    // Validate required fields
    if (!companyDetails.companyName?.trim()) {
      errors.push('Företagsnamn är obligatoriskt');
    } else if (!validateCompanyName(companyDetails.companyName)) {
      errors.push('Företagsnamn måste vara mellan 2-100 tecken och innehålla giltiga tecken');
    }

    if (!companyDetails.orgNumber?.trim()) {
      errors.push('Organisationsnummer är obligatoriskt');
    } else if (!validateSwedishOrgNumber(companyDetails.orgNumber)) {
      errors.push('Organisationsnummer måste vara ett giltigt svenskt organisationsnummer (XXXXXX-XXXX)');
    }

    if (!companyDetails.accountingMethod) {
      errors.push('Bokföringsmetod måste väljas');
    }

    if (!companyDetails.vatReportingPeriod) {
      errors.push('Momsrapporteringsperiod måste väljas');
    }

    // Validate optional but provided fields
    if (companyDetails.postalCode?.trim() && !validateSwedishPostalCode(companyDetails.postalCode)) {
      errors.push('Postnummer måste vara 5 siffror (t.ex. 11122)');
    }

    if (companyDetails.street?.trim() && companyDetails.street.trim().length < 3) {
      errors.push('Gatuadress måste vara minst 3 tecken om den anges');
    }

    if (companyDetails.city?.trim() && companyDetails.city.trim().length < 2) {
      errors.push('Stad måste vara minst 2 tecken om den anges');
    }

    return errors;
  };

  const validateSingleField = (fieldName, value) => {
    switch (fieldName) {
      case 'companyName':
        if (!value?.trim()) return 'Företagsnamn är obligatoriskt';
        if (!validateCompanyName(value)) return 'Företagsnamn måste vara mellan 2-100 tecken och innehålla giltiga tecken';
        break;
      case 'orgNumber':
        if (!value?.trim()) return 'Organisationsnummer är obligatoriskt';
        if (!validateSwedishOrgNumber(value)) return 'Organisationsnummer måste vara ett giltigt svenskt organisationsnummer (XXXXXX-XXXX)';
        break;
      case 'postalCode':
        if (value?.trim() && !validateSwedishPostalCode(value)) return 'Postnummer måste vara 5 siffror (t.ex. 11122)';
        break;
      case 'street':
        if (value?.trim() && value.trim().length < 3) return 'Gatuadress måste vara minst 3 tecken om den anges';
        break;
      case 'city':
        if (value?.trim() && value.trim().length < 2) return 'Stad måste vara minst 2 tecken om den anges';
        break;
      default:
        break;
    }
    return null;
  };

  // SIE file handling functions
  const handleSieFileUpload = async (file) => {
    setSieLoading(true);
    setSieError(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(getApiUrl('auth/extract-sie-data'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setExtractedData(response.data.extractedData);
        setSieFile(file);
        
        // Pre-populate form with extracted data
        setCompanyDetails(prev => ({
          ...prev,
          companyName: response.data.extractedData.companyName || prev.companyName,
          orgNumber: response.data.extractedData.orgNumber || prev.orgNumber,
          street: response.data.extractedData.address || prev.street,
          city: response.data.extractedData.city || prev.city,
          postalCode: response.data.extractedData.postalCode || prev.postalCode,
          accountingMethod: response.data.extractedData.accountingMethod || prev.accountingMethod,
          vatReportingPeriod: response.data.extractedData.vatReportingPeriod || prev.vatReportingPeriod,
        }));

        swal('Success', 'SIE data extracted successfully! Form has been pre-filled with extracted information.', 'success');
      } else {
        setSieError('Failed to extract SIE data');
        swal('Error', 'Failed to extract SIE data', 'error');
      }
    } catch (error) {
      console.error('SIE extraction error:', error);
      setSieError('Failed to extract SIE data');
      swal('Error', 'Failed to extract SIE data. Please try again.', 'error');
    } finally {
      setSieLoading(false);
    }
  };

  const handleSieFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.se') && !file.name.toLowerCase().endsWith('.sie')) {
        swal('Error', 'Please select a valid SIE file (.se or .sie)', 'error');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        swal('Error', 'File size too large. Please select a file smaller than 10MB.', 'error');
        return;
      }

      handleSieFileUpload(file);
    }
  };

  const clearSieData = () => {
    setSieFile(null);
    setExtractedData(null);
    setSieError(null);
  };

  const handleMethodSelection = (method) => {
    setRegistrationMethod(method);
    // Reset states when switching methods
    setSelectedCompany(null);
    setSearchResults([]);
    setInputValue('');
    clearSieData();
    
    // If switching to SIE method, keep the form in manual mode
    if (method === 'sie') {
      setManualEntry(true);
    } else if (method === 'manual') {
      setManualEntry(true);
    } else if (method === 'search') {
      setManualEntry(false);
    }
  };

  const handleBackToMethodSelection = () => {
    setRegistrationMethod(null);
    setSelectedCompany(null);
    setSearchResults([]);
    setInputValue('');
    setManualEntry(false);
    clearSieData();
    setCompanyDetails({
      companyName: '',
      orgNumber: '',
      street: '',
      postalCode: '',
      city: '',
      accountingMethod: '',
      vatReportingPeriod: '',
    });
  };

  const onCreateCompany = async (e) => {
    e.preventDefault();

    // Validate form data
    const validationErrors = validateFormData();

    if (validationErrors.length > 0) {
      // Show all validation errors
      const errorMessage = validationErrors.join('\n• ');
      swal('Oops', `Följande fel måste rättas:\n• ${errorMessage}`, "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const createNewCompanyApiUrl = getApiUrl('auth/company');

      // Clean and format the org number for API submission
      const cleanedOrgNumber = companyDetails.orgNumber.replace(/\D/g, '');
      const formattedOrgNumber = `${cleanedOrgNumber.slice(0, 6)}-${cleanedOrgNumber.slice(6)}`;

      const response = await axios.post(createNewCompanyApiUrl, {
        companyName: companyDetails.companyName.trim(),
        googleId: uid,
        orgNumber: formattedOrgNumber,
        legalEntityType: manualEntry ? 'Aktiebolag' : selectedCompany ? selectedCompany.document.legalEntityType : 'Aktiebolag',
        street: companyDetails.street?.trim() || '',
        postalCode: companyDetails.postalCode?.replace(/\s/g, '') || '', // Remove spaces from postal code
        city: companyDetails.city?.trim() || '',
        accountingMethod: companyDetails.accountingMethod,
        vatReportingPeriod: companyDetails.vatReportingPeriod,
      });

      console.log('Company added successfully:', response.data);
      swal('Success', 'Företaget har registrerats framgångsrikt!', 'success');
      history.push('/select-company');

    } catch (error) {
      swal('Oops', 'Något gick fel. Försök igen senare.', "error");
      console.error('Error adding company:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const searchCompany = async (inputValue) => {
    setSelectedCompany(null);
    setCompanyDetails({
      companyName: '',
      orgNumber: '',
      street: '',
      postalCode: '',
      city: '',
      accountingMethod: '',
      vatReportingPeriod: '',
    });
    setSearchResults([]);
    setLoading(true);

    const cleanedInput = inputValue.replace(/\s+/g, '');
    const isNumber = /^\d+$/.test(cleanedInput);

    const apiUrl = isNumber
      ? `https://api.tic.io/search/companies?q=${encodeURIComponent(cleanedInput)}&query_by=registrationNumber`
      : `https://api.tic.io/search/companies?query_by=names.nameOrIdentifier&q=${encodeURIComponent(inputValue)}`;

    const options = {
      method: 'GET',
      headers: { 'x-api-key': 'k7ITvPSmQvIasmvuNxrRFLZ29xhpbIzB' },
      url: apiUrl,
      skipAuth: true, // External API - don't send auth token
    };

    try {
      const response = await axios(options);
      setSearchResults(response.data.hits);
    } catch (err) {
      swal('Oops', 'Något gick fel. Försök igen senare.', "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setSearchResults([]);

    console.log('Selected company:', company);

    setCompanyDetails({
      companyName: company.document.names[0].nameOrIdentifier,
      orgNumber: company.document.registrationNumber,
      street: company.document.mostRecentRegisteredAddress.streetAddress,
      postalCode: company.document.mostRecentRegisteredAddress.postalCode,
      city: company.document.mostRecentRegisteredAddress.city,
      accountingMethod: '',
      vatReportingPeriod: '',
    });
  };

  const toggleManualEntry = () => {
    setManualEntry(!manualEntry);
    setSelectedCompany(null);
    setCompanyDetails({
      companyName: '',
      orgNumber: '',
      street: '',
      postalCode: '',
      city: '',
      accountingMethod: '',
      vatReportingPeriod: '',
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setCompanyDetails({
      ...companyDetails,
      [name]: value,
    });

    // Clear error for this field and validate in real-time
    const updatedErrors = { ...fieldErrors };
    delete updatedErrors[name];

    // Only show validation errors after user has interacted with the field and it has content or was required
    if (value.trim() || ['companyName', 'orgNumber', 'accountingMethod', 'vatReportingPeriod'].includes(name)) {
      const error = validateSingleField(name, value);
      if (error) {
        updatedErrors[name] = error;
      }
    }

    setFieldErrors(updatedErrors);
  };

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .form-input:focus,
        .form-select:focus {
          outline: none;
          border-color: #44814e;
          box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
          background-color: white;
        }
        
        .search-button:hover:not(:disabled),
        .submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 10px 25px rgba(68, 129, 78, 0.3);
        }
        
        .search-button:disabled,
        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .form-input.error,
        .form-select.error {
          border-color: #ef4444;
          background-color: #fef2f2;
        }
        
        .form-input.error:focus,
        .form-select.error:focus {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        .error-message {
          color: #ef4444;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: block;
        }
        
        .back-button:hover {
          color: #374151 !important;
        }
        
        .manual-toggle:hover {
          color: #3d6f46 !important;
        }
      `}</style>

      <div style={styles.newCompanyPage} data-testid="new-company-page">
        <div style={getResponsiveStyle(styles.pageWrapper, mobileStyles.pageWrapper, mobile)} data-testid="page-wrapper">
          {/* Left Panel */}
          <div className="left-panel" style={getResponsiveStyle(styles.leftPanel, mobileStyles.leftPanel, mobile)} data-testid="left-panel">
            <div style={{ textAlign: 'center', maxWidth: '400px' }} data-testid="branding-content">
              <h1 style={{
                fontSize: '3rem',
                fontWeight: '700',
                marginBottom: '1rem',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
              }} data-testid="app-title">
                LedgerLoop AI
              </h1>
              <p style={{
                fontSize: '1.25rem',
                marginBottom: '3rem',
                opacity: 0.95,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                lineHeight: 1.4
              }} data-testid="app-description">
                Starta smart – automatisera ekonomin från dag ett med AI-driven bokföring
              </p>

              {/* Simple footer */}
              <div style={{
                marginTop: 'auto',
                fontSize: '0.875rem',
                opacity: 0.8,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                <p style={{ marginBottom: '1rem' }}>© 2025 Riza Consulting AB</p>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <a
                    href="https://www.ledgerloop.ai/privacy"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Integritetspolicy
                  </a>
                  <span style={{ opacity: 0.7 }}>•</span>
                  <a
                    href="https://www.ledgerloop.ai/terms"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                  >
                    Användarvillkor
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="right-panel" style={getResponsiveStyle(styles.rightPanel, mobileStyles.rightPanel, mobile)} data-testid="right-panel">
            <div style={getResponsiveStyle(styles.contentCard, mobileStyles.contentCard, mobile)} data-testid="content-card">
              {/* Header */}
              <div style={styles.headerSection} data-testid="header-section">
                <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }} data-testid="form-title">
                  Registrera nytt företag
                </h2>
                <p style={{ color: '#6b7280', fontSize: '1rem' }} data-testid="form-description">
                  Sök efter ditt företag i Bolagsverkets register eller ange uppgifterna manuellt
                </p>
              </div>

              <form onSubmit={onCreateCompany} data-testid="new-company-form" role="form" noValidate>
                
                {/* Method Selection Screen */}
                {!registrationMethod && (
                  <div style={{ marginBottom: '2rem' }}>
                    <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '2rem', textAlign: 'center' }}>
                      Välj hur du vill registrera ditt företag
                    </p>

                    {/* Method Cards */}
                    <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                      
                      {/* Search in Register */}
                      <div
                        onClick={() => handleMethodSelection('search')}
                        style={{
                          padding: '1.5rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: 'white'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#44814e';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(68, 129, 78, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Search size={24} color="#44814e" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                              Sök i Bolagsverkets register
                            </h3>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                              Hitta ditt företag i det officiella registret och få information automatiskt ifylld
                            </p>
                          </div>
                          <ArrowLeft size={20} style={{ transform: 'rotate(180deg)', color: '#9ca3af', flexShrink: 0 }} />
                        </div>
                      </div>

                      {/* Upload SIE File - Only show if feature flag is enabled */}
                      {isSieUploadEnabled && (
                        <div
                          onClick={() => handleMethodSelection('sie')}
                          style={{
                            padding: '1.5rem',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            background: 'white'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#44814e';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(68, 129, 78, 0.15)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <FileText size={24} color="#44814e" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                                Ladda upp SIE-fil
                              </h3>
                              <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                                Importera din bokföringsfil och få all företagsinformation automatiskt extraherad
                              </p>
                            </div>
                            <ArrowLeft size={20} style={{ transform: 'rotate(180deg)', color: '#9ca3af', flexShrink: 0 }} />
                          </div>
                        </div>
                      )}

                      {/* Manual Entry */}
                      <div
                        onClick={() => handleMethodSelection('manual')}
                        style={{
                          padding: '1.5rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          background: 'white'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#44814e';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(68, 129, 78, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            <Edit3 size={24} color="#44814e" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', marginBottom: '0.5rem' }}>
                              Ange uppgifter manuellt
                            </h3>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                              Fyll i all företagsinformation själv från grunden
                            </p>
                          </div>
                          <ArrowLeft size={20} style={{ transform: 'rotate(180deg)', color: '#9ca3af', flexShrink: 0 }} />
                        </div>
                      </div>
                    </div>

                    {/* Back Button on Method Selection */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
                      <button
                        className="back-button"
                        style={styles.backButton}
                        type="button"
                        onClick={() => history.goBack()}
                        disabled={isSubmitting}
                      >
                        <ArrowLeft size={16} />
                        Tillbaka
                      </button>
                    </div>
                  </div>
                )}

                {/* Company Search Section */}
                {registrationMethod === 'search' && !selectedCompany && (
                  <div style={{ marginBottom: '2rem' }} data-testid="search-section">
                    <div style={styles.formGroup}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="company-search-input">
                        Sök företag
                      </label>
                      <div className="search-section" style={{ display: 'flex', gap: '1rem', alignItems: 'end' }}>
                        <div style={{ flex: 1 }}>
                          <div style={styles.inputWrapper}>
                            <Search size={20} style={styles.inputIcon} />
                            <input
                              id="company-search-input"
                              className="form-input"
                              style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                              value={inputValue}
                              onChange={(e) => setInputValue(e.target.value)}
                              type="text"
                              placeholder="Företagsnamn eller organisationsnummer"
                              disabled={isSubmitting || loading}
                              data-testid="search-input"
                            />
                          </div>
                        </div>
                        <button
                          className="search-button"
                          style={styles.searchButton}
                          onClick={() => searchCompany(inputValue)}
                          type="button"
                          disabled={loading || isSubmitting || !inputValue.trim()}
                          data-testid="search-button"
                        >
                          {loading ? (
                            <>
                              <div style={styles.loadingSpinner} data-testid="search-loading-spinner"></div>
                              Söker...
                            </>
                          ) : (
                            <>
                              <Search size={16} />
                              Sök
                            </>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div style={styles.searchResults} data-testid="search-results">
                        <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', fontWeight: '600', color: '#374151' }} data-testid="search-results-header">
                          Sökresultat ({searchResults.length})
                        </div>
                        {searchResults.map((company, idx) => (
                          <div
                            key={idx}
                            style={{
                              ...styles.companyResult,
                              ...(hoveredResult === idx ? styles.companyResultHover : {}),
                              ...(searchResults.length === idx + 1 ? { borderBottom: 'none' } : {})
                            }}
                            onMouseEnter={() => setHoveredResult(idx)}
                            onMouseLeave={() => setHoveredResult(null)}
                            onClick={() => handleCompanySelect(company)}
                            data-testid={`company-result-${idx}`}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              <Building2 size={20} style={{ color: '#44814e' }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }} data-testid={`company-name-${idx}`}>
                                  {company.document.names[0].nameOrIdentifier}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#6b7280' }} data-testid={`company-org-number-${idx}`}>
                                  Org.nr: {company.document.registrationNumber}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      className="back-button"
                      style={{...styles.backButton, marginTop: '1.5rem'}}
                      type="button"
                      onClick={handleBackToMethodSelection}
                      disabled={isSubmitting}
                    >
                      <ArrowLeft size={16} />
                      Tillbaka
                    </button>
                  </div>
                )}

                {/* SIE File Upload Section */}
                {registrationMethod === 'sie' && (
                  <div style={{ marginBottom: '2rem' }}>
                    <div style={{ padding: '1.5rem', border: '1px solid #e5e7eb', borderRadius: '12px', background: '#f9fafb' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                        <FileText size={24} color="#44814e" />
                        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: 0 }}>
                          Ladda upp SIE-fil
                        </h3>
                      </div>
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
                        Ladda upp din SIE-fil för att automatiskt extrahera företagsinformation och bokföringsinställningar.
                      </p>
                      
                      {!sieFile ? (
                        <div>
                          <input
                            type="file"
                            accept=".se,.sie"
                            onChange={handleSieFileChange}
                            style={{ display: 'none' }}
                            id="sie-file-input"
                            disabled={sieLoading || isSubmitting}
                          />
                          <label
                            htmlFor="sie-file-input"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.875rem 1.5rem',
                              background: '#44814e',
                              color: 'white',
                              borderRadius: '8px',
                              cursor: sieLoading || isSubmitting ? 'not-allowed' : 'pointer',
                              opacity: sieLoading || isSubmitting ? 0.6 : 1,
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              transition: 'all 0.2s ease',
                              border: 'none'
                            }}
                          >
                            {sieLoading ? (
                              <>
                                <div style={styles.loadingSpinner}></div>
                                Analyserar SIE-fil...
                              </>
                            ) : (
                              <>
                                <FileText size={18} />
                                Välj SIE-fil (.se eller .sie)
                              </>
                            )}
                          </label>
                        </div>
                      ) : (
                        <div style={{ padding: '1rem', background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
                            <Check size={20} color="#16a34a" />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '600', color: '#16a34a', marginBottom: '0.25rem' }}>
                                SIE-fil analyserad framgångsrikt
                              </div>
                              <div style={{ fontSize: '0.875rem', color: '#15803d' }}>
                                {sieFile.name} • {extractedData?.analysis?.accountingMethod?.confidence}% säkerhet på bokföringsmetod
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={clearSieData}
                              style={{
                                background: 'white',
                                border: '1px solid #bbf7d0',
                                color: '#16a34a',
                                cursor: 'pointer',
                                padding: '0.5rem',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                          {extractedData && (
                            <div style={{ fontSize: '0.75rem', color: '#15803d', paddingLeft: '2rem' }}>
                              ✓ Företagsnamn, organisationsnummer och bokföringsinställningar extraherade
                            </div>
                          )}
                        </div>
                      )}

                      {sieError && (
                        <div style={{ marginTop: '1rem', padding: '0.875rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', fontSize: '0.875rem' }}>
                          <AlertCircle size={16} style={{ display: 'inline', marginRight: '0.5rem' }} />
                          {sieError}
                        </div>
                      )}
                    </div>

                    <button
                      className="back-button"
                      style={{...styles.backButton, marginTop: '1.5rem'}}
                      type="button"
                      onClick={handleBackToMethodSelection}
                      disabled={isSubmitting}
                    >
                      <ArrowLeft size={16} />
                      Tillbaka
                    </button>
                  </div>
                )}

                {/* Selected Company or Manual Entry */}
                {((registrationMethod === 'search' && selectedCompany) || (registrationMethod === 'manual') || (registrationMethod === 'sie' && sieFile)) && (
                  <>
                    {selectedCompany && (
                      <div style={{
                        background: '#f0fdf4',
                        border: '1px solid #bbf7d0',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '2rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem'
                      }} data-testid="selected-company-display">
                        <div style={styles.selectedBadge} data-testid="selected-company-badge">
                          <Check size={16} />
                          Valt företag
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '600', color: '#16a34a' }} data-testid="selected-company-name">
                            {selectedCompany.document.names[0].nameOrIdentifier}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#15803d' }} data-testid="selected-company-org-number">
                            {selectedCompany.document.registrationNumber}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Company Details Form */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }} data-testid="company-details-form">
                      <div style={styles.formGroup}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="company-name-input">
                          Företagsnamn *
                        </label>
                        <div style={styles.inputWrapper}>
                          <Building2 size={20} style={styles.inputIcon} />
                          <input
                            id="company-name-input"
                            className={`form-input ${fieldErrors.companyName ? 'error' : ''}`}
                            style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                            type="text"
                            name="companyName"
                            value={companyDetails.companyName}
                            onChange={handleInputChange}
                            placeholder="Företagsnamn"
                            disabled={isSubmitting}
                            required
                            data-testid="company-name-input"
                          />
                        </div>
                        {fieldErrors.companyName && (
                          <span className="error-message" data-testid="company-name-error">
                            {fieldErrors.companyName}
                          </span>
                        )}
                      </div>

                      <div style={styles.formGroup}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="org-number-input">
                          Organisationsnummer *
                        </label>
                        <div style={styles.inputWrapper}>
                          <FileText size={20} style={styles.inputIcon} />
                          <input
                            id="org-number-input"
                            className={`form-input ${fieldErrors.orgNumber ? 'error' : ''}`}
                            style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                            type="text"
                            name="orgNumber"
                            value={companyDetails.orgNumber}
                            onChange={handleInputChange}
                            placeholder="XXXXXX-XXXX"
                            disabled={isSubmitting}
                            required
                            data-testid="org-number-input"
                          />
                        </div>
                        {fieldErrors.orgNumber && (
                          <span className="error-message" data-testid="org-number-error">
                            {fieldErrors.orgNumber}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Address Fields */}
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                      <div style={styles.formGroup}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="street-input">
                          Gatuadress
                        </label>
                        <div style={styles.inputWrapper}>
                          <MapPin size={20} style={styles.inputIcon} />
                          <input
                            id="street-input"
                            className={`form-input ${fieldErrors.street ? 'error' : ''}`}
                            style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                            type="text"
                            name="street"
                            value={companyDetails.street}
                            onChange={handleInputChange}
                            placeholder="Gatuadress"
                            disabled={isSubmitting}
                            data-testid="street-input"
                          />
                        </div>
                        {fieldErrors.street && (
                          <span className="error-message" data-testid="street-error">
                            {fieldErrors.street}
                          </span>
                        )}
                      </div>

                      <div style={styles.formGroup}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="postal-code-input">
                          Postnummer
                        </label>
                        <input
                          id="postal-code-input"
                          className={`form-input ${fieldErrors.postalCode ? 'error' : ''}`}
                          style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                          type="text"
                          name="postalCode"
                          value={companyDetails.postalCode}
                          onChange={handleInputChange}
                          placeholder="123 45"
                          disabled={isSubmitting}
                          data-testid="postal-code-input"
                        />
                        {fieldErrors.postalCode && (
                          <span className="error-message" data-testid="postal-code-error">
                            {fieldErrors.postalCode}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={styles.formGroup}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="city-input">
                        Stad
                      </label>
                      <input
                        id="city-input"
                        className={`form-input ${fieldErrors.city ? 'error' : ''}`}
                        style={getResponsiveStyle(styles.formInput, mobileStyles.formInput, mobile)}
                        type="text"
                        name="city"
                        value={companyDetails.city}
                        onChange={handleInputChange}
                        placeholder="Stad"
                        disabled={isSubmitting}
                        data-testid="city-input"
                      />
                      {fieldErrors.city && (
                        <span className="error-message" data-testid="city-error">
                          {fieldErrors.city}
                        </span>
                      )}
                    </div>

                    {/* Accounting Settings */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                      <div style={styles.formGroup}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="accounting-method-select">
                          Bokföringsmetod *
                        </label>
                        <select
                          id="accounting-method-select"
                          className="form-select"
                          style={getResponsiveStyle(styles.formSelect, mobileStyles.formSelect, mobile)}
                          name="accountingMethod"
                          value={companyDetails.accountingMethod}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          required
                          data-testid="accounting-method-select"
                        >
                          <option value="">Välj bokföringsmetod</option>
                          <option value="cash">Kontantmetoden</option>
                          <option value="accrual">Fakturametoden</option>
                        </select>
                      </div>

                      <div style={styles.formGroup}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }} htmlFor="vat-reporting-period-select">
                          Momsredovisningsperiod *
                        </label>
                        <select
                          id="vat-reporting-period-select"
                          className="form-select"
                          style={getResponsiveStyle(styles.formSelect, mobileStyles.formSelect, mobile)}
                          name="vatReportingPeriod"
                          value={companyDetails.vatReportingPeriod}
                          onChange={handleInputChange}
                          disabled={isSubmitting}
                          required
                          data-testid="vat-reporting-period-select"
                        >
                          <option value="">Välj period</option>
                          <option value="monthly">Månadsvis</option>
                          <option value="quarterly">Kvartalsvis</option>
                          <option value="yearly">Årsvis</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                {(manualEntry || selectedCompany) && (
                  <div style={{ textAlign: 'center', marginTop: '2rem' }} data-testid="form-actions">
                    <button
                      className="submit-button"
                      style={styles.submitButton}
                      type="submit"
                      disabled={isSubmitting}
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <div style={styles.loadingSpinner} data-testid="submit-loading-spinner"></div>
                          Registrerar företag...
                        </div>
                      ) : (
                        <>
                          <Check size={16} />
                          Registrera företag
                        </>
                      )}
                    </button>

                    <button
                      className="back-button"
                      style={styles.backButton}
                      type="button"
                      onClick={handleBackToMethodSelection}
                      disabled={isSubmitting}
                      data-testid="back-button"
                    >
                      <ArrowLeft size={16} />
                      Tillbaka
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default NewCompany;
